import { ANNOUNCEMENT_TYPE_LABEL, isAnnouncementType, type AnnouncementType } from './enums';

/** One row of `GET /api/announcements` (`{ data, pagination }`, pinned first, newest first, with `author` included). */
export interface ApiAnnouncement {
  id: string;
  firmId: string;
  title: string;
  /** Announcement body. */
  message: string;
  /** Free-text in the schema; the API expects info|warning|update|maintenance. */
  type: string;
  /** Free-text in the schema; the API expects low|normal|high|urgent. */
  priority: string;
  isPinned: boolean;
  /** Global flag, not per user. */
  isRead: boolean;
  authorId: string;
  createdAt: string;
  author: { id: string; name: string; email: string; avatar: string | null } | null;
}

export interface AnnouncementView {
  id: string;
  title: string;
  body: string;
  /** Known API type, or 'unknown' for a type this build has no label for. */
  type: AnnouncementType | 'unknown';
  typeLabel: string;
  priority: string;
  isPinned: boolean;
  isRead: boolean;
  author: string;
  /** No API source yet (Phase 4 gap); always '—'. */
  authorRole: string;
  authorInitials: string;
  authorAvatar: string | null;
  /** ISO timestamp of creation. */
  timestamp: string;
}

const UNKNOWN_AUTHOR = 'Unknown';
const NO_AUTHOR_INITIALS = '?';
const NO_ROLE = '—';

function initialsOf(name: string): string {
  return name
    .split(/\s+/)
    .filter(Boolean)
    .map((part) => part[0])
    .join('')
    .slice(0, 2)
    .toUpperCase();
}

export function toAnnouncementView(api: ApiAnnouncement): AnnouncementView {
  const knownType = isAnnouncementType(api.type) ? api.type : null;
  const authorName = api.author?.name ?? UNKNOWN_AUTHOR;
  return {
    id: api.id,
    title: api.title,
    body: api.message,
    type: knownType ?? 'unknown',
    typeLabel: knownType ? ANNOUNCEMENT_TYPE_LABEL[knownType] : api.type,
    priority: api.priority,
    isPinned: api.isPinned,
    isRead: api.isRead,
    author: authorName,
    authorRole: NO_ROLE,
    authorInitials: api.author ? initialsOf(api.author.name) : NO_AUTHOR_INITIALS,
    authorAvatar: api.author?.avatar ?? null,
    timestamp: api.createdAt,
  };
}

/**
 * Sidebar badge: unread rows in the loaded page. Zero (no badge) until the request has succeeded, so loading
 * and failed requests show none, even when stale rows are still cached.
 */
export function countUnreadAnnouncements(result: { isSuccess: boolean; data?: { data: ApiAnnouncement[] } }): number {
  if (!result.isSuccess || result.data === undefined) return 0;
  return result.data.data.filter((announcement) => !announcement.isRead).length;
}
